import { ChangeDetectionStrategy, Component, computed, inject, OnInit, signal, input } from '@angular/core';
import { auditTime, distinctUntilChanged, filter, Observable } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ColumnDefinitionV3, FilterTableSettings, HierarchyTableV3Component, TableServiceV3 } from '@gea/digital-ui-lib';
import { Fix_ACTION, getFaultDefinitions } from '@fault-analysis/table-config/fault-analysis-column-definitions';
import { sortTreeNodes, toTreeNodes } from '@fault-analysis/table-config/table.util';
import { TranslatePipe } from '@ngx-translate/core';
import { BaseFault, FaultCatalogType, Annunciation, AnnunciationTree, MachineFaultDto } from '@shared/models';
import { TranslateEnumService, TroubleshootingService } from '@shared/services';
import { InfoBoxComponent, UpdateTimeComponent, SpinnerComponent } from '@shared/components';
import { FaultsStore, OrganizationsStore } from '@shared/state';
import { handleTableTreeFilters, invertSortDirectionsForClientSorting } from '@shared/helpers';
import { BRCBaseComponent } from '@brc/hrt-base-component';
import { isEqual } from 'lodash';

const generateText = (type: FaultCatalogType, text: 'TITLE' | 'TOOLTIP') => `FAULT_ANALYSIS.${type.toUpperCase()}S.${text}`;

export const FAULT_ANALYSIS_TABLE_FILTERS = 'FAULT_ANALYSIS_TABLE_FILTERS';

@Component({
  selector: 'gea-hrt-fault-analysis-section',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./section.component.scss'],
  imports: [InfoBoxComponent, TranslatePipe, UpdateTimeComponent, HierarchyTableV3Component, SpinnerComponent],
  template: `
    <gea-hrt-info-box [boxTitle]="title()" [tooltip]="tooltip()" [withBorder]="true">
      <p class="small-font">
        {{ 'FAULT_ANALYSIS.DATA_EXTRACTED_ON' | translate }}:
        <gea-hrt-update-time [date]="currentDate" />
      </p>

      @if (isLoading()) {
        <gea-hrt-spinner [diameter]="40" />
      } @else {
        <div class="table-container-section">
          @if (parsedData().length) {
            <geaui-hierarchy-table-v3
              [tableId]="TABLE_ID()"
              class="table"
              [columnDefinitions]="columnDefinitions()"
              [sortable]="true"
              [data]="parsedData()"
              [persistSettings]="false" />
          } @else {
            @let dateRange = selectedDateRange();
            <p class="error-msg">
              {{
                'FAULT_ANALYSIS.NO_FAULT'
                  | translate
                    : {
                        type: placeholder(),
                        dateRange: dateRange === 1 ? 24 : (dateRange ?? 0) / 7,
                      }
              }}

              {{ 'FAULT_ANALYSIS.NO_FAULT.DATE_UNIT.' + (dateRange === 1 ? 'HOUR' : 'WEEK') | translate }}
              :hoorray!
            </p>
          }
        </div>
      }
    </gea-hrt-info-box>
  `,
})
export class FaultAnalysisSectionComponent extends BRCBaseComponent implements OnInit {
  readonly isLoading = computed(() => !!this.faultsStore.isLoading());
  readonly rowData = signal<AnnunciationTree[]>([]);
  private readonly tableSettings = signal<FilterTableSettings>(this.getStoredSettings());
  readonly parsedData = computed(() => {
    const tableSettings = invertSortDirectionsForClientSorting(this.tableSettings());
    const filteredData = handleTableTreeFilters<MachineFaultDto, AnnunciationTree>(tableSettings, this.rowData());
    return sortTreeNodes(tableSettings, filteredData);
  });
  private readonly faultsStore = inject(FaultsStore);
  readonly selectedDateRange = this.faultsStore.selectedDateRange;
  readonly troubleshootingService = inject(TroubleshootingService);
  private readonly organizationsStore = inject(OrganizationsStore);

  readonly annunciations$ = input.required<Observable<Annunciation[] | AnnunciationTree[] | undefined>>();

  readonly type = input.required<FaultCatalogType>();
  readonly TABLE_ID = computed(() => `${this.type()}-table` as const);
  readonly placeholder = computed(() => this.translateService.GetTranslationPlural(this.type()));
  readonly title = computed(() => generateText(this.type(), 'TITLE'));
  readonly tooltip = computed(() => generateText(this.type(), 'TOOLTIP'));
  readonly columnDefinitions = computed<ColumnDefinitionV3[]>(() => getFaultDefinitions(this.type()));
  public currentDate = new Date();

  private readonly tableService = inject(TableServiceV3);
  private readonly translateService = inject(TranslateEnumService);

  ngOnInit(): void {
    // 1) Data feed (flat or tree) -> normalize to NEW nodes (extensible)
    this.annunciations$()
      .pipe(
        filter((data): data is Annunciation[] | AnnunciationTree[] => Array.isArray(data)),
        distinctUntilChanged(isEqual), // upstream guards
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((result) => {
        const nodes = toTreeNodes(result); // returns NEW cloned nodes
        this.rowData.set(nodes);
      });

    // 2) Settings -> apply sort to current nodes
    this.tableService
      .getFilterTableSettings(this.TABLE_ID())
      .pipe(
        // break sync feedback with the service; schedule on next macrotask
        auditTime(0),
        // deep-ish dedupe to avoid loops (adjust isEqual if you have one)
        distinctUntilChanged((a, b) => isEqual(a.columns, b.columns)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((settings) => {
        const tableSettings = this.getEffectiveTableSettings(settings);
        this.tableSettings.set(tableSettings);
        sessionStorage.setItem(FAULT_ANALYSIS_TABLE_FILTERS, JSON.stringify(tableSettings));
      });

    // 3) Row actions (unwrap node.data when coming from hierarchy table)
    this.tableService.actions
      .pipe(
        filter((action) => action.tableId === this.TABLE_ID()),
        filter((action) => action.action === Fix_ACTION),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((action) => {
        const row: BaseFault = (action.rowData?.data ?? action.rowData) as BaseFault;
        void this.resolveFaultCode(row);
      });
  }

  async resolveFaultCode(baseFault: BaseFault): Promise<boolean> {
    try {
      const { faultCode, machineId } = baseFault;
      const organizationId = this.organizationsStore.organizationId();

      document.querySelector('.sidebar ul li div.p-tree-node-selected')?.classList.remove('p-tree-node-selected');
      document
        .querySelector('.sidebar ul li[aria-label="TAB_CONTAINER.TABS.TROUBLESHOOTING"] div')
        ?.classList.add('p-tree-node-selected');

      this.troubleshootingService.setTroubleshootingFilters({ machine: machineId, faultCode });
      return await this.router.navigate([`troubleshooting/${organizationId}`], {
        queryParams: { faultCode, machine: machineId },
      });
    } catch {
      return false;
    }
  }

  private getStoredSettings(): FilterTableSettings {
    const storedFilter = sessionStorage.getItem(FAULT_ANALYSIS_TABLE_FILTERS);
    return storedFilter ? JSON.parse(storedFilter) : { columns: {} };
  }

  private getEffectiveTableSettings(settings: FilterTableSettings): FilterTableSettings {
    if (!Object.entries(settings.columns ?? {}).length) {
      const storedSettings = this.tableSettings();
      if (Object.entries(storedSettings.columns ?? {}).length) {
        return storedSettings;
      }
    }
    return settings;
  }
}
